import dayjs from "dayjs";

// Shared by the new-reservation modal, the series list and the cancel flow so
// the Spanish labels and the range rules of a turno fijo live in one place. The
// backend stays the source of truth (helper/reservations/courtReservationSeries.js):
// the range rules here only give feedback before the request.

export const MAX_SERIES_WEEKS = 26;
export const DEFAULT_SERIES_WEEKS = 8;
const DATE_FORMAT = "YYYY-MM-DD";

// What the organizer reads for each date of a preview or of a 409 skipped list.
const DATE_STATE_LABELS = {
	available: "Disponible",
	reserved: "Ocupado",
	blocked: "Bloqueado",
	outside_hours: "Fuera de horario",
	closed: "Cerrado",
	past: "Pasado",
	unavailable: "No disponible",
};

// Reasons the backend gives for a date it left untouched when cancelling a series.
const KEPT_REASON_LABELS = {
	charged: "ya cobrado",
	checked_in: "con check-in",
};

// Same numbering as the backend (Date#getUTCDay): 0 is Sunday.
const WEEKDAY_NAMES = [
	"Domingo",
	"Lunes",
	"Martes",
	"Miércoles",
	"Jueves",
	"Viernes",
	"Sábado",
];

/** Label for one preview/skipped entry; an entry without reason is available. */
export const seriesDateStateLabel = (entry) => {
	if (entry?.available) return DATE_STATE_LABELS.available;
	return DATE_STATE_LABELS[entry?.reason] || DATE_STATE_LABELS.unavailable;
};

export const keptReasonLabel = (reason) =>
	KEPT_REASON_LABELS[reason] || "se mantiene";

export const weekdayName = (weekday) => WEEKDAY_NAMES[weekday] || "";

/** "Jueves 22/10" for a YYYY-MM-DD date; the raw value when it is not a date. */
export const seriesDateLabel = (isoDate) => {
	const day = dayjs(isoDate);
	if (!day.isValid()) return isoDate || "-";
	return `${WEEKDAY_NAMES[day.day()]} ${day.format("DD/MM")}`;
};

export const shortSeriesDate = (isoDate) => {
	const day = dayjs(isoDate);
	return day.isValid() ? day.format("DD/MM/YYYY") : isoDate || "-";
};

/** Default end of a new series: eight weeks after its first date. */
export const defaultSeriesDateTo = (dateFrom) =>
	dayjs(dateFrom).add(DEFAULT_SERIES_WEEKS, "week").format(DATE_FORMAT);

/** Latest end the backend accepts: 26 weeks after the first date. */
export const maxSeriesDateTo = (dateFrom) =>
	dayjs(dateFrom).add(MAX_SERIES_WEEKS * 7, "day");

/** Error text for a range the backend would refuse, or null when it is fine. */
export const validateSeriesRange = (dateFrom, dateTo) => {
	const from = dayjs(dateFrom);
	const to = dayjs(dateTo);
	if (!from.isValid() || !to.isValid()) return "Elegí hasta qué fecha se repite.";
	if (to.isBefore(from, "day")) {
		return "La fecha final no puede ser anterior a la primera.";
	}
	if (to.isAfter(maxSeriesDateTo(dateFrom), "day")) {
		return `Un turno fijo dura como máximo ${MAX_SERIES_WEEKS} semanas.`;
	}
	return null;
};

export const countAvailable = (dates) =>
	(Array.isArray(dates) ? dates : []).filter((entry) => entry?.available).length;

export const pluralTurnos = (count) =>
	`${count} ${count === 1 ? "turno" : "turnos"}`;

const pluralFechas = (count) =>
	`${count} ${count === 1 ? "fecha omitida" : "fechas omitidas"}`;

/** Toast after creating a series: created and skipped counts. */
export const seriesCreatedMessage = (result) => {
	const created = Array.isArray(result?.created) ? result.created.length : 0;
	const skipped = Array.isArray(result?.skipped) ? result.skipped.length : 0;
	return skipped > 0
		? `Turno fijo creado: ${pluralTurnos(created)} (${pluralFechas(skipped)}).`
		: `Turno fijo creado: ${pluralTurnos(created)}.`;
};

/** Toast after cancelling a series (or part of it). */
export const seriesCancelledMessage = (result) => {
	const cancelled = Array.isArray(result?.cancelled)
		? result.cancelled.length
		: 0;
	return cancelled > 0
		? `Se cancelaron ${pluralTurnos(cancelled)} del turno fijo.`
		: "No había turnos para cancelar.";
};
